import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import InputAdornment from '@mui/material/InputAdornment';
import MuiLink from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid2';
import { useNavigate } from 'react-router-dom';
import BusinessIcon from '@mui/icons-material/Business';
import DevicesIcon from '@mui/icons-material/Devices';
import RouterIcon from '@mui/icons-material/Router';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HistoryIcon from '@mui/icons-material/History';
import ListAltIcon from '@mui/icons-material/ListAlt';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import SearchIcon from '@mui/icons-material/Search';
import TimelineIcon from '@mui/icons-material/Timeline';

import {
  activeAdminTenantId,
  activeParticipantMembership,
  participantTenants,
  suspendedTenantNames,
  useAuth,
  userHasRole,
} from '../auth/AuthContext';
import * as tenantsApi from '../api/tenants';
import { useFormatDateTime, useFormatTime } from '../util/displayTimeZone';
import type { Participant, Tenant } from '../api/tenants';
import * as proceduresApi from '../api/procedures';
import * as devicesApi from '../api/devices';
import * as appliancesApi from '../api/appliances';
import * as baselinesApi from '../api/baselines';
import * as cognitiveApi from '../api/cognitive';
import * as masterBaselinesApi from '../api/masterBaselines';
import * as motionApi from '../api/motion';
import * as sessionsApi from '../api/sessions';
import * as liveApi from '../api/live';
import { extractErrorMessage } from '../api/errors';
import { ExportDataButton } from '../components/ExportDataButton';
import { AiSpendCard } from '../components/AiSpendCard';
import { EmailDeliveryCard } from '../components/EmailDeliveryCard';
import { StatValue } from '../components/StatValue';

/** Grid span that fits all six tenant overview cards on one row
 *  (6-across on md+, 3 on sm, 2 on xs). */
/**
 * Padding for the eight dashboard summary cards.
 *
 * MUI's CardContent ships 16px all round plus 24px on the last child, which
 * across a row of eight short cards is more padding than content. Halved —
 * and the last-child rule has to be overridden explicitly or the 24px stays.
 */
const COMPACT_CARD_CONTENT = {
  px: 1.5,
  py: 1.25,
  '&:last-child': { pb: 1.25 },
} as const;

const SIX_ACROSS = { xs: 6, sm: 4, md: 2 };

/** The tenant Email-delivery card sits inline among the overview tiles
 *  (after the participant stats, before the domain cards). On md the grid runs
 *  on an 18-column base so all eight cards share ONE row: seven stat/domain
 *  tiles at 2/18 each (14) + this card at 4/18 (4) = 18. Wider than a tile so
 *  its sparkline + breakdown stay readable. */
const EMAIL_CARD_SIZE = { xs: 12, sm: 6, md: 4 };

/** Superadmin overview, grouped into bands (DASH-LAYOUT): the KPI trio
 *  and the ops-health trio each fill a row 3-across on their own, so the
 *  two kinds of card don't crowd into one overflowing strip. */
const KPI_SIZE = { xs: 6, sm: 4 };
const OPS_SIZE = { xs: 12, sm: 6, md: 4 };
import { AttachmentsDialog } from '../components/AttachmentsDialog';
import { HandTrajectoryPanel } from '../components/HandTrajectoryPanel';
import { CognitivePerformanceGrid } from '../components/CognitivePerformanceGrid';
import { useSessionPipelines } from '../hooks/useSessionPipelines';
import { CognitiveTrace } from '../components/CognitiveTrace';
import { SessionMetricScorecard } from '../components/SessionMetricScorecard';
import { AiCoachCard } from '../components/AiCoachCard';
import { RecordingFilters } from '../components/RecordingFilters';
import {
  filterOptions,
  isFilterActive,
  matchesFilter,
} from '../components/recordingFilterRules';
import { useFilterParams } from '../components/useFilterParams';
import {
  mergeOptions,
  toSessionQuery,
  useDebounced,
  useMySessionFilterOptions,
} from '../components/serverFilter';
import { PipelineStatusCard } from '../components/PipelineStatusCard';
import { RefreshButton } from '../components/RefreshButton';
import { SessionReplayDialog } from '../components/SessionReplayDialog';
import { useInsightsPolling } from '../hooks/useInsightsPolling';
import { TenantCognitiveSummary } from '../components/TenantCognitiveSummary';
import { PendingBaselineCard } from '../components/PendingBaselineCard';
import {
  CaptureTimeline,
  buildTimelineEvents,
  groupByDevice,
  pipelineGroups,
  withPipelines,
  type TimelineEvent,
} from '../components/CaptureTimeline';
import { formatTrialLabel, trialSeverity } from '../util/trial';
import { LicenseBanner } from '../components/license/LicenseBanner';
import { tenantCardCounts } from '../util/tenantStats';

/**
 * Role-aware landing page.
 *
 * Superadmin: total tenants (active / suspended) + a grid of tenant
 * cards, each clickable through to the tenant detail page. Per-card
 * participant breakdown comes free from the GET /tenants endpoint
 * (which now joins memberships).
 *
 * Tenant admin: their tenant's participant breakdown + quick-link
 * cards to Tenant Settings and Participants (those pages exist as
 * routes but aren't in the sidebar anymore — the hierarchy
 * Tenant → Participants is what the dashboard surfaces).
 *
 * Participant: greeting + tenant context + an empty-state placeholder
 * for the capture-sessions list that the recorder app will sync here
 * once session-upload is wired through.
 *
 * The hierarchy refactor is roadmap item P0 #4 follow-up; ROADMAP.md
 * explains why these pages moved out of the sidebar.
 */
import { pickDefaultParticipant } from './dashboardDefaults';

export function Dashboard() {
  const { user } = useAuth();
  // Suspended-tenant banner — rendered above whichever role-specific
  // dashboard the user lands on. Multi-tenant users see one banner
  // per suspended org so they know which side is gated; single-tenant
  // users get the same explanation for the otherwise-empty page.
  const suspended = suspendedTenantNames(user);
  const banner =
    suspended.length > 0 ? <SuspendedTenantBanner names={suspended} /> : null;

  // Dispatch most-privileged → least: a user who happens to be both
  // tenant_admin and participant should land on the admin view.
  if (userHasRole(user, 'superadmin')) {
    return (
      <Stack spacing={3}>
        {banner}
        <SuperadminDashboard />
      </Stack>
    );
  }
  // tenant_admin and coordinator both land on the tenant (operational)
  // dashboard — a coordinator resolves the same tenant context and sees
  // the participant roster; admin-only quick-links simply aren't
  // actionable for them (TENANT-VIEWER-ROLE).
  if (userHasRole(user, 'tenant_admin') || userHasRole(user, 'coordinator')) {
    return (
      <Stack spacing={3}>
        {banner}
        <TenantAdminDashboard />
      </Stack>
    );
  }
  if (userHasRole(user, 'participant')) {
    return (
      <Stack spacing={3}>
        {banner}
        <ParticipantDashboard />
      </Stack>
    );
  }
  // No effective role. If the banner is showing, it already
  // explains why — suppress the generic fallback so the user
  // doesn't see two competing messages ("contact 10NEWTONS" vs
  // "contact your tenant admin"). Only fall through to the
  // generic alert when there's no suspended-tenant story at all.
  if (banner) {
    return <Stack spacing={3}>{banner}</Stack>;
  }
  return (
    <Alert severity="warning">
      Your account doesn't currently hold an active role. If this is
      unexpected, sign out and back in — or contact your tenant admin.
    </Alert>
  );
}

// Customer-facing contact for tenant lifecycle questions. Kept in
// sync with the contact email shown on the About page.
const SUPPORT_EMAIL = 'help@10newtons.com';

function SuspendedTenantBanner({ names }: { names: string[] }) {
  const isOne = names.length === 1;
  const heading = isOne
    ? `Your access to ${names[0]} is suspended`
    : 'Your tenant access is suspended';
  return (
    <Alert severity="warning">
      <AlertTitle sx={{ fontWeight: 700 }}>{heading}</AlertTitle>
      {!isOne && (
        <Box sx={{ mb: 1 }}>
          The affected organisations are{' '}
          <strong>{names.join(', ')}</strong>.
        </Box>
      )}
      While the suspension is in place, your tenant's data and tools
      are hidden — participants, procedures, sessions, and insights
      will all show empty. Reach 10NEWTONS at{' '}
      <MuiLink href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</MuiLink>{' '}
      to discuss reactivating your tenant.
    </Alert>
  );
}

// ----------------------------------------------------------------------
// Superadmin
// ----------------------------------------------------------------------


/**
 * One tenant's summary card in the superadmin Dashboard grid: name +
 * status, slug, optional trial chip, and two rows of counts (participant
 * lifecycle + domain totals). Clicking opens the tenant detail page.
 */
function TenantSummaryCard({
  tenant: t,
  onOpen,
}: {
  tenant: tenantsApi.Tenant;
  onOpen: () => void;
}) {
  const { active, pending, suspended } = tenantCardCounts(t);
  // Trial chip color — trialSeverity returns 'info' | 'warning' | 'error';
  // map onto MUI's Chip palette ('info' → 'primary' so it doesn't get lost
  // next to the green success chip).
  const trialChipColor = t.trial_ends_at
    ? (
        {
          info: 'primary',
          warning: 'warning',
          error: 'error',
        } as const
      )[trialSeverity(t.trial_ends_at)]
    : undefined;
  return (
    <Card>
      <CardActionArea onClick={onOpen}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1}>
            <BusinessIcon color="primary" fontSize="small" />
            <Typography variant="subtitle1" sx={{ flex: 1, fontWeight: 700 }}>
              {t.name}
            </Typography>
            {t.is_active ? (
              <Chip label="Active" size="small" color="success" />
            ) : (
              <Chip label="Suspended" size="small" color="warning" />
            )}
          </Stack>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontFamily: 'monospace' }}
          >
            {t.slug}
          </Typography>
          {t.trial_ends_at && (
            <Box sx={{ mt: 1 }}>
              <Chip
                size="small"
                variant="outlined"
                color={trialChipColor}
                label={formatTrialLabel(t.trial_ends_at)}
              />
            </Box>
          )}
          <Stack direction="row" spacing={2} sx={{ mt: 1.5 }}>
            <CountBlock label="Participants" value={t.participant_count} />
            <CountBlock label="Active" value={active} color="success.main" />
            <CountBlock label="Pending" value={pending} color="warning.main" />
            {suspended > 0 && (
              <CountBlock
                label="Suspended"
                value={suspended}
                color="text.disabled"
              />
            )}
          </Stack>
          {/* Second row of stats — the domain counts (procedures, devices,
              sessions) the api returns alongside the participant counts.
              Devices uses the active-count breakdown since that's what an
              operator skims for (matches the per-tenant Dashboard's Devices
              card). */}
          <Stack direction="row" spacing={2} sx={{ mt: 1.5 }}>
            <CountBlock label="Procedures" value={t.procedure_count} />
            <CountBlock
              label="Devices"
              value={t.device_active_count}
              color="success.main"
            />
            {/* Edge capacity, same in-service definition the tenant's own
                Dashboard uses. Only shown for tenants that have any — a
                column of zeros on the tenants that never deployed edge is
                noise on a list you skim. */}
            {(t.appliance_count ?? 0) > 0 && (
              <CountBlock label="Appliances" value={t.appliance_count ?? 0} />
            )}
            {(t.camera_count ?? 0) > 0 && (
              <CountBlock label="Cameras" value={t.camera_count ?? 0} />
            )}
            <CountBlock label="Sessions" value={t.session_count} />
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

function SuperadminDashboard() {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState<tenantsApi.Tenant[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  // The name filter applies to the per-tenant grid only — the aggregate
  // stats row at the top always reflects the full dataset so the operator
  // sees the platform-wide picture regardless of what they've narrowed to.
  // Case-insensitive substring against tenant.name. (The old A-Z alphabet
  // filter was removed — search covers it, and it read dated; richer facets
  // — status / region / tier — are the direction once the roster is large.)
  const [nameFilter, setNameFilter] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  const reload = useCallback(async () => {
    setError(null);
    setRefreshing(true);
    try {
      const rows = await tenantsApi.listTenants();
      setTenants(rows);
    } catch (err: unknown) {
      setError(extractErrorMessage(err, 'failed to load tenants'));
      setTenants([]);
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const filteredTenants = useMemo(() => {
    if (!tenants) return [];
    const needle = nameFilter.trim().toLowerCase();
    if (!needle) return tenants;
    return tenants.filter((t) => t.name.toLowerCase().includes(needle));
  }, [tenants, nameFilter]);

  if (error) {
    return (
      <Alert severity="error" onClose={() => setError(null)}>
        {error}
      </Alert>
    );
  }
  if (tenants === null) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  const total = tenants.length;
  const active = tenants.filter((t) => t.is_active).length;
  const suspended = total - active;
  const filtersActive = nameFilter.trim() !== '';
  const clearFilters = () => setNameFilter('');

  return (
    <Stack
      spacing={3}
      sx={{
        // Tenant grid can grow arbitrarily — cap to the viewport
        // (minus topbar + outer page padding) and scroll internally
        // so the bottom row of cards doesn't get clipped on long
        // tenant rosters.
        maxHeight: 'calc(100vh - 112px)',
        overflowY: 'auto',
        pr: 1,
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <Typography variant="h4">Dashboard</Typography>
        <RefreshButton
          onClick={() => void reload()}
          refreshing={refreshing}
        />
      </Stack>

      {/* All five overview cards on one row (md+): three tenant quick-stats
          (span 2 each) + the two operational cards (span 3 each, a little
          wider for their sparklines) = 12 columns. Wraps to 3/2 on sm and
          stacks on xs. DASH-PIPELINE-HEALTH + DASH-AI-SPEND each own their
          polling cadence. */}
      {/* KPI band — the tenant summary numbers. */}
      <Grid container spacing={2}>
        <StatCard
          label="Tenants"
          value={total}
          subtext="total"
          size={KPI_SIZE}
        />
        <StatCard
          label="Active"
          value={active}
          subtext="tenants"
          color="success.main"
          size={KPI_SIZE}
        />
        <StatCard
          label="Suspended"
          value={suspended}
          subtext="tenants"
          color="warning.main"
          size={KPI_SIZE}
        />
      </Grid>

      {/* Ops-health band — the polling monitors, grouped as their own
          row of three so they read as a set (each owns its cadence). */}
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ mt: 1, display: 'block', letterSpacing: '0.08em' }}
      >
        Platform health
      </Typography>
      <Grid container spacing={2}>
        <PipelineStatusCard size={OPS_SIZE} />
        <AiSpendCard size={OPS_SIZE} />
        <EmailDeliveryCard size={OPS_SIZE} />
      </Grid>

      {/* Per-tenant grid */}
      <Typography variant="h6" sx={{ mt: 2 }}>
        Tenants
      </Typography>

      {/* Filter row — only shown when there's something to filter.
          Hide on an empty roster so the empty-state guidance below
          isn't crowded by an inert search box. */}
      {tenants.length > 0 && (
        <TextField
          size="small"
          placeholder="Search tenants"
          value={nameFilter}
          onChange={(e) => setNameFilter(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ maxWidth: 360 }}
        />
      )}

      {tenants.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No tenants yet. Use the Tenants page to create one.
          </Typography>
        </Paper>
      ) : filteredTenants.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Stack alignItems="center" spacing={1}>
            <Typography color="text.secondary">
              No tenants match the current filter.
            </Typography>
            <Chip
              label="Clear filters"
              size="small"
              clickable
              onClick={clearFilters}
              disabled={!filtersActive}
            />
          </Stack>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {filteredTenants.map((t) => (
            <Grid key={t.id} size={{ xs: 12, sm: 6, md: 3 }}>
              <TenantSummaryCard
                tenant={t}
                onOpen={() => navigate(`/admin/tenants/${t.id}`)}
              />
            </Grid>
          ))}
        </Grid>
      )}
    </Stack>
  );
}

// ----------------------------------------------------------------------
// Tenant admin
// ----------------------------------------------------------------------

function TenantAdminDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const tenantId = activeAdminTenantId(user);
  const [participants, setParticipants] = useState<Participant[] | null>(null);
  // Tenant detail is fetched in parallel with the participants list
  // so the trial banner (when a trial is set) renders without an
  // extra round-trip. A failure to load the tenant doesn't block the
  // page — the participant cards are the primary content; the banner
  // just won't appear.
  const [tenant, setTenant] = useState<Tenant | null>(null);
  // Procedures / Devices / Sessions are fetched alongside so the
  // domain cards can render counts. Each failure is swallowed
  // independently — the operator still sees the rest of the dashboard
  // even if one endpoint hiccups. The cards render an em-dash for
  // an absent count.
  const [procedures, setProcedures] = useState<
    proceduresApi.Procedure[] | null
  >(null);
  const [devices, setDevices] = useState<devicesApi.Device[] | null>(null);
  const [appliances, setAppliances] = useState<
    appliancesApi.Appliance[] | null
  >(null);
  const [cameras, setCameras] = useState<appliancesApi.Camera[] | null>(null);
  const [sessions, setSessions] = useState<sessionsApi.CaptureSession[] | null>(
    null,
  );
  const [cognitiveSummary, setCognitiveSummary] =
    useState<cognitiveApi.CognitiveTenantSummary | null>(null);
  // The redesigned dashboard has no procedure picker; the cognitive summary
  // fetch (which powers the "Computing insights" chip) + the pending-baseline
  // notice run tenant-wide.
  const procedureFilter = '';
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  // The cross-participant Compare moved to Analysis → Benchmark; the admin
  // dashboard is the Individual view.

  // Tenant switch: drop the previous tenant's data in the same render
  // (React's "adjust state on a prop change" pattern). Otherwise the new
  // tenant briefly renders with the old participants + sessions, the
  // summary picks its default participant from them, and asks the NEW
  // tenant for the OLD tenant's participant — a 404 and a blank panel
  // (prod 2026-10-09, DARPA participant requested under ACEP).
  const [seenTenant, setSeenTenant] = useState(tenantId);
  if (tenantId !== seenTenant) {
    setSeenTenant(tenantId);
    setParticipants(null);
    setTenant(null);
    setProcedures(null);
    setDevices(null);
    setAppliances(null);
    setCameras(null);
    setSessions(null);
    setCognitiveSummary(null);
    setError(null);
  }
  // A slow response for the tenant we just left must not land on this one.
  const currentTenant = useRef(tenantId);
  currentTenant.current = tenantId;

  const reload = useCallback(() => {
    if (!tenantId) return;
    const forThisTenant =
      <T,>(set: (v: T) => void) =>
      (v: T) => {
        if (currentTenant.current === tenantId) set(v);
      };
    setError(null);
    setRefreshing(true);
    // Five parallel fetches — each settles independently so a slow
    // endpoint can't block the rest. We flip refreshing off when
    // every promise has settled (success or failure).
    const tasks = [
      tenantsApi
        .listParticipants(tenantId)
        .then(forThisTenant(setParticipants))
        .catch((err: unknown) => {
          if (currentTenant.current !== tenantId) return;
          setError(extractErrorMessage(err, 'failed to load participants'));
          setParticipants([]);
        }),
      tenantsApi
        .getTenant(tenantId)
        .then(forThisTenant(setTenant))
        .catch(() => {
          // Non-fatal — see the comment on the `tenant` state above.
        }),
      proceduresApi
        .listProcedures(tenantId)
        .then(forThisTenant(setProcedures))
        .catch(() => forThisTenant(setProcedures)([])),
      devicesApi
        .listDevices(tenantId)
        .then(forThisTenant(setDevices))
        .catch(() => forThisTenant(setDevices)([])),
      // Edge capacity for the Edge card. Failures fall back to an empty list
      // like the others, so a tenant without edge reads as zero rather than
      // leaving the card stuck on "loading".
      appliancesApi
        .listAppliances(tenantId)
        .then(forThisTenant(setAppliances))
        .catch(() => forThisTenant(setAppliances)([])),
      appliancesApi
        .listCameras(tenantId)
        .then(forThisTenant(setCameras))
        .catch(() => forThisTenant(setCameras)([])),
      sessionsApi
        .listCaptureSessions(tenantId)
        .then(forThisTenant(setSessions))
        .catch(() => forThisTenant(setSessions)([])),
      cognitiveApi
        .getTenantCognitiveSummary(tenantId, procedureFilter || null)
        .then(forThisTenant(setCognitiveSummary))
        // Non-fatal — the summary only powers the "Computing insights" chip.
        .catch(() => {}),
    ];
    void Promise.allSettled(tasks).then(() => setRefreshing(false));
  }, [tenantId, procedureFilter]);

  useEffect(() => {
    reload();
  }, [reload]);

  // Tenant-side smart-polling. The cognitive summary's overall
  // session_count is what the dashboard aggregates from; while
  // sessions.length > overall.session_count there's an upload waiting
  // for the worker. Refetch the cognitive + motion summaries every
  // 7 s until they catch up.
  const tenantInsightsPending = useMemo(() => {
    if (sessions === null || cognitiveSummary === null) return false;
    if (sessions.length === 0) return false;
    const scoredCount = cognitiveSummary.overall.session_count;
    return sessions.length > scoredCount;
  }, [sessions, cognitiveSummary]);
  const { polling: tenantInsightsPolling } = useInsightsPolling({
    pending: tenantInsightsPending,
    onTick: () => {
      if (!tenantId) return;
      void cognitiveApi
        .getTenantCognitiveSummary(tenantId, procedureFilter || null)
        .then(setCognitiveSummary)
        .catch(() => {});
    },
  });

  // Land the Individual view on the participant whose LATEST recording is
  // the most recent — not the one with the most recordings.
  //
  // "Most recordings" was a proxy for "has data", and it worked, but it never
  // ages: the dashboard opened on the same person every day regardless of who
  // had recorded since, and reported for weeks as "it always shows the same
  // old session".
  //
  // Recency alone is not enough either. A session that is still recording, or
  // whose file has not arrived yet, carries no scores and no trace — landing
  // there gives the blank right pane this logic exists to avoid. So the rank
  // is over recordings whose file has LANDED (`s3Key`), which is the same
  // thing the download and replay affordances gate on, and the nearest thing
  // the client has to "completed". If nobody has one, fall back to recency
  // over everything, then to the old most-recordings rule, so a tenant
  // mid-first-capture still lands somewhere sensible rather than nowhere.
  //
  // Three-state on purpose (undefined | null | id): `sessions` loads AFTER
  // `participants` (the page renders as soon as participants arrive), so this
  // returns `undefined` while sessions are still loading, `null` once loaded
  // if nobody has data, else the winning id. The child must NOT lock onto a
  // participant while this is undefined — that's the race that made it always
  // land on roster[0] (a data-less participant) instead of the one with data.
  // Must sit above the early returns (stable hook order).
  const defaultParticipantId = useMemo(
    () => pickDefaultParticipant(sessions, participants),
    [sessions, participants],
  );

  if (!tenantId) {
    return (
      <Alert severity="warning">
        You don't currently hold an active tenant_admin role. If this is
        unexpected, sign out and back in to refresh your session.
      </Alert>
    );
  }
  if (error) {
    return (
      <Alert severity="error" onClose={() => setError(null)}>
        {error}
      </Alert>
    );
  }
  if (participants === null) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  const total = participants.length;
  // "Active" = onboarded (signed in at least once) AND not suspended —
  // NOT merely "not suspended". A manually-added participant is
  // active-membership from creation but stays *pending* ("Invited")
  // until they set their password / sign in (has_signed_in).
  const active = participants.filter(
    (p) => p.is_active && p.has_signed_in,
  ).length;
  const pending = participants.filter(
    (p) => p.is_active && !p.has_signed_in,
  ).length;

  // Domain counts. `null` means the fetch hasn't completed (or
  // failed); the DomainCard renders an em-dash in that case. Devices
  // count `active` as anything other than retired/suspended/lost so
  // it matches what an operator would consider "in service."
  const procedureCount = procedures?.length ?? null;
  const deviceTotal = devices?.length ?? null;
  const deviceActive =
    devices?.filter((d) => d.status === 'active').length ?? null;
  const sessionCount = sessions?.length ?? null;
  // Edge capacity: what could record today. Retired appliances and cameras
  // are excluded for the same reason the API excludes them — a retired box
  // is not capacity.
  const liveAppliances =
    appliances?.filter((a) => a.status !== 'retired') ?? null;
  const applianceTotal = liveAppliances?.length ?? null;
  // A camera only counts if its appliance is still live. Re-enrolling an
  // appliance retires the old row and leaves its cameras behind still marked
  // active, so counting cameras alone reports boxes that are long gone.
  const cameraTotal =
    cameras === null || liveAppliances === null
      ? null
      : cameras.filter(
          (c) =>
            c.status !== 'retired' &&
            liveAppliances.some((a) => a.id === c.appliance_id),
        ).length;
  const sessionsRecentWeek = sessions
    ? sessions.filter((s) => {
        const start = new Date(s.started_at).getTime();
        return Date.now() - start < 7 * 24 * 60 * 60 * 1000;
      }).length
    : null;

  return (
    <Stack spacing={3}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
      >
        <Stack>
          <Typography variant="h4">Dashboard</Typography>
          <Typography color="text.secondary">
            Quick overview of your tenant. Use the sidebar to manage
            participants, procedures, devices, and review captured
            sessions.
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          {tenantInsightsPolling && (
            <Chip
              size="small"
              color="primary"
              variant="outlined"
              icon={<CircularProgress size={12} thickness={6} />}
              label="Computing insights…"
            />
          )}
          <RefreshButton onClick={reload} refreshing={refreshing} />
        </Stack>
      </Stack>

      {/* License warning (TENANT-LICENSES; trials are licenses too):
          expiring within 45 days, ended, read-only or over a limit. */}
      {tenant && <LicenseBanner tenantId={tenant.id} />}

      {/* Participant counts — each card click-throughs to the
          Participants page for consistency with the domain cards
          below. The three-way breakdown (total / active / suspended)
          stays since it's more useful at-a-glance than a single
          summary. */}
      {/* All six overview counts on one row (6-across on md+, 3 on sm,
          2 on xs) — Participants / Active / Suspended quick-stats plus the
          Procedures / Devices / Sessions domain quick-links. Counts are
          derived from the parallel list fetches above. */}
      {/* 18-col base on md so all eight cards fit ONE row (seven tiles at
          2/18 + the email card at 4/18). Default (stretch) alignment so every
          card matches the tallest (the Email-delivery card) — one even row. */}
      <Grid
        container
        spacing={2}
        columns={{ xs: 12, sm: 12, md: 18 }}
      >
        <StatCard
          label="Participants"
          value={total}
          subtext="total"
          size={SIX_ACROSS}
          onClick={() => navigate('/tenant/participants')}
        />
        <StatCard
          label="Active"
          value={active}
          subtext="signed in"
          color="success.main"
          size={SIX_ACROSS}
          onClick={() => navigate('/tenant/participants')}
        />
        <StatCard
          label="Pending"
          value={pending}
          subtext="not signed in"
          color="warning.main"
          size={SIX_ACROSS}
          onClick={() => navigate('/tenant/participants')}
        />
        {/* This tenant's outbound-email health (invitations, welcomes,
            session-ready notices), placed inline after the participant
            stats. Wider than the stat tiles so the sparkline reads. */}
        <EmailDeliveryCard tenantId={tenantId} size={EMAIL_CARD_SIZE} />
        <DomainCard
          icon={<ListAltIcon />}
          label="Procedures"
          primary={procedureCount}
          size={SIX_ACROSS}
          secondary={
            procedureCount === null
              ? 'loading'
              : procedureCount === 0
                ? 'none defined yet'
                : 'in catalogue'
          }
          onClick={() => navigate('/tenant/procedures')}
        />
        <DomainCard
          icon={<DevicesIcon />}
          label="Devices"
          primary={deviceTotal}
          size={SIX_ACROSS}
          secondary={
            deviceTotal === null
              ? 'loading'
              : deviceTotal === 0
                ? 'none enrolled yet'
                : `${deviceActive ?? 0} active`
          }
          onClick={() => navigate('/tenant/devices')}
        />
        {/* Edge capacity. Appliances lead because that is what an operator
            deploys and troubleshoots; the camera count rides as the subtext
            because a camera only exists through an appliance. */}
        <DomainCard
          icon={<RouterIcon />}
          label="Edge"
          primary={applianceTotal}
          size={SIX_ACROSS}
          secondary={
            applianceTotal === null
              ? 'loading'
              : applianceTotal === 0
                ? 'none paired yet'
                : `${cameraTotal ?? 0} camera${cameraTotal === 1 ? '' : 's'}`
          }
          onClick={() => navigate('/tenant/appliances')}
        />
        <DomainCard
          icon={<HistoryIcon />}
          label="Sessions"
          primary={sessionCount}
          size={SIX_ACROSS}
          secondary={
            sessionCount === null
              ? 'loading'
              : sessionCount === 0
                ? 'none captured yet'
                : `${sessionsRecentWeek ?? 0} this week`
          }
          onClick={() => navigate('/tenant/activity')}
        />
      </Grid>

      {/* Cognitive performance summary — ROADMAP #43.1 phase 1B.
          Overall + per-participant chart with operator-selectable
          metric, plus placeholders for Bimanual Dexterity (#43.2)
          and Mastery Rate (#44). Procedure filter is shared across
          the cards + chart (state lives here so future sibling
          panels can join the same filter). */}
      {/* Provisional pending-baseline overlay (#3) — the tenant's own
          submitted-but-unapproved cohort baseline for the selected
          procedure, labelled as not-yet-driving the Faculty bars. */}
      <PendingBaselineCard tenantId={tenantId} procedure={procedureFilter} />

      <TenantCognitiveSummary
        tenantId={tenantId}
        participants={participants}
        defaultParticipantId={defaultParticipantId}
        // Compare moved to Analysis → Benchmark; the dashboard is Individual.
        mode="individual"
      />
    </Stack>
  );
}

function DomainCard({
  icon,
  label,
  primary,
  secondary,
  onClick,
  size = { xs: 12, sm: 4 },
}: {
  icon: React.ReactNode;
  label: string;
  primary: number | null;
  secondary: string;
  onClick: () => void;
  /** Grid column span. Defaults to 3-across (sm: 4). */
  size?: React.ComponentProps<typeof Grid>['size'];
}) {
  return (
    <Grid size={size} sx={{ display: 'flex' }}>
      <Card sx={{ width: '100%' }}>
        <CardActionArea onClick={onClick} sx={{ height: '100%' }}>
          {/* Same compact two-row shape as StatCard so the row of eight
              settles on one height. */}
          <CardContent sx={COMPACT_CARD_CONTENT}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.75}
              sx={{ minWidth: 0, '& .MuiSvgIcon-root': { fontSize: 16 } }}
            >
              {icon}
              <Typography
                variant="overline"
                noWrap
                sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.3 }}
              >
                {label}
              </Typography>
            </Stack>
            <Stack
              direction="row"
              alignItems="baseline"
              spacing={0.5}
              sx={{ minWidth: 0 }}
            >
              <StatValue
                value={primary === null ? '—' : primary}
                color={primary === null ? 'text.disabled' : undefined}
                size={30}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                noWrap
                sx={{ minWidth: 0, fontSize: '0.7rem' }}
              >
                {secondary}
              </Typography>
            </Stack>
          </CardContent>
        </CardActionArea>
      </Card>
    </Grid>
  );
}

// ----------------------------------------------------------------------
// Participant
// ----------------------------------------------------------------------

function ParticipantDashboard() {
  const { user, refresh: refreshUser } = useAuth();
  const fmtTime = useFormatTime();
  const membership = activeParticipantMembership(user);
  // ROADMAP #54: when the participant belongs to 2+ tenants, offer a
  // "this tenant / all tenants" scope toggle. We store the MODE, not a
  // tenant id, so "this tenant" always tracks the active topbar
  // switcher (which `activeParticipantMembership` already honours).
  const isMultiTenant = participantTenants(user).length >= 2;
  const [tenantScope, setTenantScope] = useState<'this' | 'all'>(
    isMultiTenant ? 'this' : 'all',
  );
  // Same key as the timeline's filter bar below — same URL params, so no
  // prop drilling and no chance of the two drifting apart.
  const [ownFilter] = useFilterParams('act');
  const serverFilter = useDebounced(ownFilter);
  const [sessions, setSessions] = useState<sessionsApi.CaptureSession[] | null>(
    null,
  );
  const [baselines, setBaselines] = useState<baselinesApi.Baseline[] | null>(
    null,
  );
  const [cognitive, setCognitive] = useState<
    cognitiveApi.CognitiveScore[] | null
  >(null);
  const [cognitiveError, setCognitiveError] = useState<string | null>(null);
  const [motion, setMotion] = useState<motionApi.MotionScore[] | null>(null);
  /** ROADMAP #44 reference catalogue. Loaded once (it's a small
   *  curated table); null while in-flight, empty array on error. */
  const [masterBaselines, setMasterBaselines] = useState<
    masterBaselinesApi.MasterBaseline[]
  >([]);
  // '' = "All procedures". Drives the cognitive-scores refetch so
  // the backend can return the narrowed feed (matches the tenant
  // Dashboard pattern; cleaner than client-side filtering when the
  // cap eventually rises past 200 rows).
  const [procedureFilter, setProcedureFilter] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  // Historical Performance Summary sits below the session content now and is
  // collapsible — most participants don't need the by-procedure breakdown
  // every login. Default open so nothing's hidden; users can fold it.
  const [showPerfSummary, setShowPerfSummary] = useState(true);
  // In-progress sessions recording for this participant right now, so
  // they can watch the live stream from their own dashboard (same
  // viewer the tenant admin uses; server gates on ownership).
  const [liveSessions, setLiveSessions] = useState<
    liveApi.LiveSessionSummary[]
  >([]);
  const [liveWatch, setLiveWatch] = useState<{
    sessionId: string;
    title: string;
  } | null>(null);

  // Single source-of-truth fetcher — re-used by the on-mount load,
  // the procedure-filter effect, and the Refresh button. Motion +
  // cognitive errors stay soft (the grid degrades to no-data rows);
  // sessions errors surface inline because the activity timeline
  // can't render without them.
  const reload = useCallback(() => {
    if (!membership) return;
    setError(null);
    setCognitiveError(null);
    setRefreshing(true);
    const tasks = [
      sessionsApi
        // Narrowed in SQL, before the 200-row cap. Reading the same URL
        // params the timeline's own filter bar writes is deliberate: the URL
        // IS the shared state, so the control and the query stay in step
        // without threading a callback through three components.
        .listMyCaptureSessions(toSessionQuery(serverFilter))
        .then(setSessions)
        .catch((err: unknown) => {
          setError(extractErrorMessage(err, 'failed to load sessions'));
          setSessions([]);
        }),
      // Baselines load independently — ROADMAP #15 E3.
      baselinesApi
        .listMyBaselines()
        .then(setBaselines)
        .catch(() => setBaselines([])),
      cognitiveApi
        .listMyCognitiveScores(200, procedureFilter || null)
        .then(setCognitive)
        .catch((err: unknown) => {
          setCognitiveError(
            extractErrorMessage(err, 'failed to load cognitive scores'),
          );
          setCognitive([]);
        }),
      motionApi
        .listMyMotionScores(200, procedureFilter || null)
        .then(setMotion)
        .catch(() => setMotion([])),
      // ROADMAP #44 reference catalogue — small, never narrowed by
      // the procedure filter. Soft failure: the grid degrades to
      // empty Faculty bars.
      masterBaselinesApi
        .listMasterBaselines()
        .then(setMasterBaselines)
        .catch(() => setMasterBaselines([])),
    ];
    void Promise.allSettled(tasks).then(() => setRefreshing(false));
    // Re-fetches when the filter changes — the narrowing is a query now,
    // not a pass over cached rows.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [membership, procedureFilter, JSON.stringify(serverFilter)]);

  // The Refresh button re-pulls the data feeds AND /users/me, so the
  // grid's metric visibility tracks a tenant admin's latest
  // scorecard_hidden_metrics change — it rides on the membership, not
  // on the session/score feeds, so `reload()` alone wouldn't pick it
  // up. Kept out of `reload` itself: refreshUser() mints a new user
  // object → new membership ref → new reload identity, which would
  // re-fire the mount effect and loop.
  const handleRefresh = useCallback(() => {
    void refreshUser().catch(() => {});
    reload();
  }, [refreshUser, reload]);

  useEffect(() => {
    reload();
  }, [reload]);

  // Poll for a session streaming for this participant right now, so the
  // "Live now" strip appears within a few seconds of the kiosk starting
  // to record them (mirrors the tenant Activity page). Own sessions only.
  useEffect(() => {
    if (!membership) return;
    let active = true;
    const tick = async () => {
      try {
        const rows = await liveApi.listMyLiveSessions();
        if (active) setLiveSessions(rows);
      } catch {
        if (active) setLiveSessions([]);
      }
    };
    void tick();
    const id = setInterval(tick, 5000);
    return () => {
      active = false;
      clearInterval(id);
    };
  }, [membership]);

  // Participant Compare / benchmarking moved to Analysis → Benchmark
  // (getMyCompareScaffold + ParticipantCompareWelcome now live there). The
  // dashboard is the participant's individual view.

  // Smart-polling: while any uploaded session has no matching
  // cognitive_score row yet, refetch the cognitive + motion feeds
  // every 7 s so the trace card + grid auto-populate when the worker
  // finishes. Stops as soon as every session has a score, gets
  // capped by the hook itself, and pauses on tab-hide.
  const insightsPending = useMemo(() => {
    if (sessions === null || cognitive === null) return false;
    if (sessions.length === 0) return false;
    const scored = new Set(cognitive.map((c) => c.session_id));
    return sessions.some((s) => !scored.has(s.session_id));
  }, [sessions, cognitive]);
  const { polling: insightsPolling } = useInsightsPolling({
    pending: insightsPending,
    onTick: () => {
      void cognitiveApi
        .listMyCognitiveScores(200, procedureFilter || null)
        .then(setCognitive)
        .catch(() => {});
      void motionApi
        .listMyMotionScores(200, procedureFilter || null)
        .then(setMotion)
        .catch(() => {});
    },
  });

  if (!membership) {
    return (
      <Alert severity="warning">
        Your participant role is currently suspended. If this is
        unexpected, contact your tenant admin.
      </Alert>
    );
  }

  // Greeting falls back to the email local-part when the user hasn't
  // filled in a name yet (which is common right after invite-accept).
  const greetingName =
    user?.full_name?.trim() || user?.email?.split('@')[0] || 'there';
  // Initials for the identity avatar — first+last of the name, else the
  // email's first letter.
  const initials = (() => {
    const name = user?.full_name?.trim();
    if (name) {
      const parts = name.split(/\s+/);
      return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : ''))
        .toUpperCase();
    }
    return (user?.email?.[0] ?? '?').toUpperCase();
  })();

  // ROADMAP #54: filter the four sections to the active tenant when the
  // scope is "this" (and the participant is multi-tenant). The feeds are
  // fetched across all tenants once; we filter client-side so toggling
  // is instant and the rows already carry `tenant_id`.
  const effectiveTenantId =
    tenantScope === 'this' && isMultiTenant ? membership.tenant_id : null;
  const inScope = (tid: string | null | undefined) =>
    effectiveTenantId === null || tid === effectiveTenantId;
  const scopedSessions = (sessions ?? []).filter((s) => inScope(s.tenant_id));
  const scopedBaselines = (baselines ?? []).filter((b) => inScope(b.tenant_id));
  const scopedCognitive = (cognitive ?? []).filter((c) => inScope(c.tenant_id));
  const scopedMotion = (motion ?? []).filter((m) => inScope(m.tenant_id));

  // Participant Compare / empty-state gating (COMPARE-CONFIG). The "We are
  // ready for your data" empty state shows for ANY participant with no
  // sessions yet — a first-timer should always see the benchmarks and the
  // message, even when the tenant hasn't enabled participant Compare. The
  // interactive Compare view (the toggle + the cohort comparison) stays gated
  // on the per-tenant switch, and only once the participant actually has data.

  return (
    <Stack spacing={3}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
      >
        <Stack direction="row" spacing={2} alignItems="center">
          {/* Identity strip — makes this feel like the participant's own
              dashboard, not a generic page. Title/tenant/session-count are
              all real; a composite readiness % would need a metric we don't
              have yet, so it's intentionally absent. */}
          <Avatar
            sx={{
              width: 56,
              height: 56,
              bgcolor: 'primary.main',
              color: '#fff',
              fontWeight: 800,
              fontSize: '1.35rem',
            }}
          >
            {initials}
          </Avatar>
          <Stack>
            <Typography variant="h4" sx={{ lineHeight: 1.15 }}>
              Welcome, {greetingName}
            </Typography>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mt: 0.5, flexWrap: 'wrap', rowGap: 0.5 }}
            >
              {user?.title && (
                <Chip size="small" variant="outlined" label={user.title} />
              )}
              <Typography variant="body2" color="text.secondary">
                {membership.tenant_name}
              </Typography>
              {sessions !== null && (
                <Typography variant="body2" color="text.secondary">
                  · {scopedSessions.length} session
                  {scopedSessions.length === 1 ? '' : 's'}
                </Typography>
              )}
            </Stack>
          {isMultiTenant && (
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mt: 1 }}
            >
              <Typography variant="body2" color="text.secondary">
                Show:
              </Typography>
              <Chip
                size="small"
                clickable
                label={`${membership.tenant_name} only`}
                color={tenantScope === 'this' ? 'primary' : 'default'}
                variant={tenantScope === 'this' ? 'filled' : 'outlined'}
                onClick={() => setTenantScope('this')}
              />
              <Chip
                size="small"
                clickable
                label="All tenants"
                color={tenantScope === 'all' ? 'primary' : 'default'}
                variant={tenantScope === 'all' ? 'filled' : 'outlined'}
                onClick={() => setTenantScope('all')}
              />
            </Stack>
          )}
          </Stack>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <ExportDataButton scope="me" label="Export my data" />
          <RefreshButton onClick={handleRefresh} refreshing={refreshing} />
        </Stack>
      </Stack>

      {/* Benchmarking / self-vs-cohort Compare now lives in Analysis →
          Benchmark. The dashboard is the participant's individual view. */}
      <>
      {/* Live now — a session is streaming for this participant right
          now. Same real-time viewer the tenant admin uses; the server
          only lists sessions recording for THIS participant. */}
      {liveSessions.length > 0 && (
        <Card variant="outlined" sx={{ borderColor: 'error.light' }}>
          <CardContent>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mb: 1 }}
            >
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  bgcolor: 'error.main',
                }}
              />
              <Typography variant="subtitle1" fontWeight={600}>
                Live now
              </Typography>
            </Stack>
            <Stack spacing={1}>
              {liveSessions.map((s) => (
                <Stack
                  key={s.session_id}
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ flexWrap: 'wrap' }}
                >
                  <Typography variant="body2">
                    <strong>{s.procedure_name ?? 'Your session'}</strong>
                    {s.started_at
                      ? ` · started ${fmtTime(s.started_at)}`
                      : ''}
                  </Typography>
                  <Button
                    size="small"
                    variant="contained"
                    color="error"
                    startIcon={<PlayCircleOutlineIcon />}
                    onClick={() =>
                      setLiveWatch({
                        sessionId: s.session_id,
                        title: s.procedure_name ?? 'Live session',
                      })
                    }
                  >
                    Watch live
                  </Button>
                </Stack>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Single Activity card — replaces the prior split Baselines +
          Sessions cards. One chronological timeline with icons for
          each event the kiosk recorded for you; clicking an icon
          downloads the file (NDJSON when available, JSON-row dump
          for baselines that have no S3 recording on file). The
          capture-app handles re-baselining + comparison-anchor
          policy on the device; the portal just shows what's there. */}
      <Card variant="outlined">
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1}>
            <TimelineIcon color="primary" />
            <Typography variant="h6">Your activity</Typography>
            {insightsPolling && (
              <Chip
                size="small"
                color="primary"
                variant="outlined"
                icon={<CircularProgress size={12} thickness={6} />}
                label="Computing insights…"
                sx={{ ml: 1 }}
              />
            )}
          </Stack>
          {error ? (
            <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          ) : sessions === null || baselines === null ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ mt: 1 }}>
              <ParticipantTimeline
                sessions={scopedSessions}
                baselines={scopedBaselines}
                cognitiveScores={scopedCognitive}
              />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Historical performance by procedure — moved BELOW the session-
          centric content so the page opens like a coach (you → your latest
          session → what it means) rather than an analytics dashboard.
          Collapsible: strongest for multi-procedure users, foldable for the
          rest. ROADMAP #43.1: Duration + Focus live; Enjoyment + Motion
          light up in-place when #43.2 / #44 land. */}
      <Card variant="outlined">
        <CardContent>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: showPerfSummary ? 1 : 0 }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <TimelineIcon color="primary" />
              <Typography variant="h6">Your Performance Summary</Typography>
            </Stack>
            <Button
              size="small"
              onClick={() => setShowPerfSummary((o) => !o)}
              endIcon={
                <ExpandMoreIcon
                  sx={{
                    transform: showPerfSummary ? 'rotate(180deg)' : 'none',
                    transition: 'transform 0.15s',
                  }}
                />
              }
            >
              {showPerfSummary ? 'Hide' : 'Show'}
            </Button>
          </Stack>
          <Collapse in={showPerfSummary} unmountOnExit>
            <CognitivePerformanceGrid
              loading={cognitive === null && sessions === null}
              error={cognitiveError}
              cognitiveScores={scopedCognitive}
              motionScores={scopedMotion}
              sessions={scopedSessions}
              masterBaselines={masterBaselines}
              procedureFilter={procedureFilter}
              onProcedureFilterChange={setProcedureFilter}
              // Honour the tenant admin's metric-visibility choices — the
              // same keys the server strips from the scorecard.
              hiddenMetrics={membership.scorecard_hidden_metrics}
            />
          </Collapse>
        </CardContent>
      </Card>
      </>

      {liveWatch && (
        <SessionReplayDialog
          open
          onClose={() => setLiveWatch(null)}
          title={liveWatch.title}
          live={{ mine: true, sessionId: liveWatch.sessionId }}
        />
      )}
    </Stack>
  );
}

/**
 * Participant-side timeline. Cross-tenant by design — the events
 * mix every tenant the user participates in. Each tenant's download
 * needs a tenant id, but `/sessions/me` + `/baselines/me` already
 * filter by participant_user_id at the server, and each row carries
 * its tenant context through the participant_user_id linkage — for
 * the participant view we just need to route the download via the
 * event's tenant, which we look up from the row.
 *
 * Today the row doesn't include tenant_id, so we use the route the
 * participant view exposes — which is just the JSON-row dump for
 * baselines and a "view-only" state for sessions. When the
 * participant-facing presigned-download endpoints land we'll wire
 * those through here.
 */
function ParticipantTimeline({
  sessions,
  baselines,
  cognitiveScores,
}: {
  sessions: sessionsApi.CaptureSession[];
  baselines: baselinesApi.Baseline[];
  /** Full cognitive feed the dashboard already fetched — used by the
   *  inline Cognitive trace card to filter scores for the selected
   *  session. Empty / no rows = the trace card stays in its empty
   *  state. */
  cognitiveScores: cognitiveApi.CognitiveScore[];
}) {
  const fmtDateTime = useFormatDateTime();
  const allEvents = useMemo(
    () => buildTimelineEvents(sessions, baselines),
    [sessions, baselines],
  );
  // Same filter vocabulary as Activity and the case builder. The participant
  // dimension is hidden: this dashboard shows one person's own captures, so
  // offering it would be a control that can only narrow to nothing.
  // In the URL, so Back undoes a filter instead of leaving the page.
  const [filter, setFilter] = useFilterParams('act');
  const myOptions = useMySessionFilterOptions();
  const filterableEvents = useMemo(
    () =>
      allEvents.map((e) => ({
        participantLabel: e.participantLabel,
        deviceLabel: e.deviceLabel,
        procedureName: e.procedureName,
        startedAt: e.capturedAt,
      })),
    [allEvents],
  );
  // The server's list spans their whole history; the rows' list adds the
  // baselines the session query knows nothing about.
  const filterOpts = useMemo(
    () => mergeOptions(myOptions.options, filterOptions(filterableEvents)),
    [myOptions.options, filterableEvents],
  );
  const filteredEvents = useMemo(
    () =>
      allEvents.filter((e) =>
        matchesFilter(
          {
            participantLabel: e.participantLabel,
            deviceLabel: e.deviceLabel,
            procedureName: e.procedureName,
            startedAt: e.capturedAt,
          },
          filter,
        ),
      ),
    [allEvents, filter],
  );
  // Where each listed session's data is (SESSION-PIPELINE). A participant's
  // sessions can span tenants and their endpoint is per tenant, so the ids go
  // out grouped by each row's own tenant.
  const groups = useMemo(() => pipelineGroups(filteredEvents), [filteredEvents]);
  const pipelines = useSessionPipelines('me', groups);
  const events = useMemo(
    () => withPipelines(filteredEvents, pipelines),
    [filteredEvents, pipelines],
  );
  // "By device" splits the picker rail into per-device sections; a single
  // participant makes "by participant" degenerate, so the only alternative
  // to the flat timeline here is device grouping.
  const [groupMode, setGroupMode] = useState<'timeline' | 'device'>(
    'timeline',
  );
  const deviceGroups = useMemo(() => groupByDevice(events), [events]);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [attachEvent, setAttachEvent] = useState<TimelineEvent | null>(null);
  const [replayEvent, setReplayEvent] = useState<TimelineEvent | null>(null);
  /** Currently-selected session in the timeline — drives the
   *  Cognitive trace card below. Baseline clicks are ignored. */
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(
    null,
  );

  // Preselect the most recent session whose file has LANDED, so the scorecard
  // + trace are populated on load instead of a blank right pane (ROADMAP
  // feedback).
  //
  // `events` is newest-first, so the old `find(kind === 'session')` took the
  // newest of any kind — including one still recording or still waiting for
  // its upload, which has no scores and no trace and renders as exactly the
  // blank pane this is here to prevent. Falls back to the newest session of
  // any state, so a tenant whose only recording is in flight still gets it
  // selected (and sees its live state) rather than nothing at all.
  // Guarded so a deliberate deselect isn't overridden.
  const autoSelectedRef = useRef(false);
  useEffect(() => {
    if (autoSelectedRef.current) return;
    const sessionsOnly = events.filter((e) => e.kind === 'session');
    const pick =
      sessionsOnly.find((e) => e.s3Key !== null) ?? sessionsOnly[0];
    if (pick) {
      setSelectedSessionId(pick.id);
      autoSelectedRef.current = true;
    }
  }, [events]);

  function handleDownload(event: TimelineEvent) {
    setDownloadingId(event.id);
    // Participant side has no tenant_id at hand (the /me endpoints
    // are cross-tenant). For baselines we always have the
    // client-side JSON-row dump as a safe fallback. For sessions,
    // until we add a /sessions/me/{id}/download endpoint, surface a
    // gentle "ask your tenant admin" — the bytes already live in S3,
    // but the participant doesn't have a presign-issuing route yet.
    try {
      if (event.kind === 'baseline') {
        downloadBaselineJsonRow(event.raw as baselinesApi.Baseline);
      }
    } finally {
      setDownloadingId(null);
    }
  }

  function handleSelect(event: TimelineEvent) {
    if (event.kind !== 'session') return; // baselines have no trace surface
    setSelectedSessionId((prev) => (prev === event.id ? null : event.id));
  }

  // Resolve the selected timeline event back to its CaptureSession row
  // — the cognitive_scores table keys on `session_id` (the capture-app's
  // free-form string), not the row UUID, so we have to pull the raw
  // record to get the right filter key.
  const selectedEvent = useMemo(
    () => events.find((e) => e.id === selectedSessionId) ?? null,
    [events, selectedSessionId],
  );
  const selectedCaptureSession =
    selectedEvent && selectedEvent.kind === 'session'
      ? (selectedEvent.raw as sessionsApi.CaptureSession)
      : null;

  // Only offer the grouping switch once captures span >1 device — a
  // single-device participant would get a pointless one-section split.
  const canGroupByDevice = deviceGroups.length > 1;
  const grouped = groupMode === 'device' && canGroupByDevice;

  return (
    <>
      {/* Filters show whenever there is anything to filter. The grouping
          toggle below is separate — it only makes sense with several devices,
          which is a different question from whether filtering is useful. */}
      {/* `isFilterActive` keeps the bar on screen when the filter matches
          nothing. Without it, narrowing to an empty window removes the only
          control that could widen it again — which now happens server-side,
          so `allEvents` itself goes empty. */}
      {(allEvents.length > 0 || isFilterActive(filter)) && (
        <RecordingFilters
          value={filter}
          onChange={setFilter}
          options={filterOpts}
          // "Your activity" is one person's own captures on their own device,
          // so participant and device can only narrow to nothing here. What is
          // worth asking is which procedure, and when.
          show={{ participants: false, devices: false }}
          summary={
            isFilterActive(filter)
              ? `${events.length} matching`
              : `${events.length} recordings`
          }
        >
          {/* Grouping rides in the same row rather than a line of its own —
              it is another "how am I looking at this" control, and a separate
              right-aligned row left the panel with three stacked bands. */}
          {canGroupByDevice && (
          <ToggleButtonGroup
            size="small"
            exclusive
            value={groupMode}
            onChange={(_, v) => v && setGroupMode(v)}
            aria-label="Activity grouping"
          >
            <ToggleButton value="timeline">
              <TimelineIcon fontSize="small" sx={{ mr: 0.5 }} />
              Timeline
            </ToggleButton>
            <ToggleButton value="device">
              <DevicesIcon fontSize="small" sx={{ mr: 0.5 }} />
              By device
            </ToggleButton>
          </ToggleButtonGroup>
          )}
        </RecordingFilters>
      )}
      {/* Right-click opens the action popup (Replay, Attach video/audio,
          baseline download); left-click still selects the session to open
          its scorecard + trace below — same UX as the tenant Dashboard
          drill-down (TenantCognitiveSummary). NO session download
          (hideSessionDownload). ROADMAP feedback. */}
      {/* Same master-detail as the tenant Dashboard: session picker (narrow
          left) beside the cognitive trace — the recording, its transcript
          and the EEG line, which is what people open a session to look at.
          The metrics and the AI-coach read on them sit underneath. */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
          // Stretch so the picker rail matches the height of the trace
          // card (which drives the row height — the rail below is taken
          // out of the height calc and scrolls internally instead).
          alignItems: 'stretch',
        }}
      >
        <Box
          sx={{
            flex: { md: '0 0 188px' },
            width: '100%',
            minWidth: 0,
            position: { md: 'relative' },
            // With a session selected the rail matches the trace card;
            // with none selected the right side is just a short hint, so
            // floor the rail height so the full picker stays usable.
            minHeight: { md: selectedCaptureSession ? 0 : 460 },
          }}
        >
          {grouped ? (
            // Device mode: normal-flow scrollable stack of per-device
            // picker sections, all feeding the same selection so the
            // detail pane keeps working across groups.
            <Box
              sx={{
                width: '100%',
                maxHeight: { md: 460 },
                overflowY: { md: 'auto' },
              }}
            >
              <Stack spacing={1.5}>
                {deviceGroups.map((group) => (
                  <Box key={group.key}>
                    <Stack
                      direction="row"
                      spacing={0.5}
                      alignItems="center"
                      sx={{ mb: 0.5 }}
                    >
                      <DevicesIcon
                        sx={{ fontSize: 14, color: 'text.secondary' }}
                      />
                      <Typography
                        variant="caption"
                        noWrap
                        sx={{ fontWeight: 700, color: 'text.secondary' }}
                      >
                        {group.label}
                      </Typography>
                    </Stack>
                    <CaptureTimeline
                      events={group.events}
                      busyId={downloadingId}
                      selectedId={selectedSessionId}
                      onSelect={handleSelect}
                      onDownload={handleDownload}
                      onAttach={(event) => setAttachEvent(event)}
                      onReplay={(event) => setReplayEvent(event)}
                      contextMenu
                      hideSessionDownload
                      orientation="vertical"
                    />
                  </Box>
                ))}
              </Stack>
            </Box>
          ) : (
            <Box
              sx={{
                width: '100%',
                position: { xs: 'static', md: 'absolute' },
                inset: { md: 0 },
              }}
            >
              <CaptureTimeline
                events={events}
                busyId={downloadingId}
                selectedId={selectedSessionId}
                onSelect={handleSelect}
                onDownload={handleDownload}
                onAttach={(event) => setAttachEvent(event)}
                onReplay={(event) => setReplayEvent(event)}
                contextMenu
                hideSessionDownload
                orientation="vertical"
                fillHeight
                groupByDate
                emptyText="No captures or baselines yet. Once the capture-app records one for you, it'll show up here."
              />
            </Box>
          )}
        </Box>

        <Box
          sx={{
            flex: { md: '1 1 0' },
            width: '100%',
            minWidth: 0,
            alignSelf: 'flex-start',
          }}
        >
          {selectedCaptureSession ? (
            <CognitiveTrace
              sessionId={selectedCaptureSession.session_id}
              sessionRowId={selectedCaptureSession.id}
              procedureName={selectedCaptureSession.procedure_name}
              capturedAt={selectedCaptureSession.started_at}
              durationS={secondsBetweenSafe(
                selectedCaptureSession.started_at,
                selectedCaptureSession.ended_at,
              )}
              cognitiveScores={cognitiveScores}
              roleName={selectedCaptureSession.role_name ?? null}
              pipeline={pipelines.get(selectedCaptureSession.session_id) ?? null}
              chartAside={(pinnedT) => (
                // 3D hand path beside the trace chart when the recording has
                // hand IMU data; renders nothing otherwise.
                <HandTrajectoryPanel
                  key={selectedCaptureSession.id}
                  sessionRowId={selectedCaptureSession.id}
                  fetchUrl={() =>
                    sessionsApi.getMySessionReplayUrl(selectedCaptureSession.id)
                  }
                  tSec={pinnedT}
                  hideWhenEmpty
                />
              )}
            />
          ) : (
            <Typography
              color="text.secondary"
              variant="body2"
              sx={{ py: 4, textAlign: 'center' }}
            >
              Select a session on the left to see its recording, trace and
              metrics.
            </Typography>
          )}
        </Box>
      </Box>
      {selectedCaptureSession && (
        <Box
          sx={{
            mt: 2,
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
            alignItems: 'flex-start',
          }}
        >
          <Box sx={{ flex: { md: '1 1 0' }, width: '100%', minWidth: 0 }}>
            <SessionMetricScorecard
              sessionId={selectedCaptureSession.session_id}
              asParticipant
            />
          </Box>
          <Box sx={{ flex: { md: '0 0 300px' }, width: '100%', minWidth: 0 }}>
            <AiCoachCard sessionId={selectedCaptureSession.session_id} />
          </Box>
        </Box>
      )}
      {attachEvent && (
        <AttachmentsDialog
          open
          onClose={() => setAttachEvent(null)}
          sessionRowId={attachEvent.id}
          sessionLabel={`${
            attachEvent.procedureName ?? 'Session'
          } · ${fmtDateTime(attachEvent.capturedAt)}`}
          // tenantId omitted → /sessions/me path
        />
      )}
      {replayEvent && (
        <SessionReplayDialog
          open
          onClose={() => setReplayEvent(null)}
          title={`${replayEvent.procedureName ?? 'Session'} · ${fmtDateTime(replayEvent.capturedAt)}`}
          fetchUrl={() => sessionsApi.getMySessionReplayUrl(replayEvent.id)}
        />
      )}
    </>
  );
}

function secondsBetweenSafe(
  start: string | null,
  end: string | null,
): number | null {
  if (!start || !end) return null;
  const ms = new Date(end).getTime() - new Date(start).getTime();
  return ms > 0 ? ms / 1000 : null;
}

function downloadBaselineJsonRow(baseline: baselinesApi.Baseline): void {
  const safeParticipant =
    (baseline.participant_label ?? 'baseline').replace(
      /[^A-Za-z0-9._-]+/g,
      '_',
    );
  const captured = baseline.captured_at.replace(/[:.]/g, '-');
  const filename = `${safeParticipant}-baseline-${captured}.json`;
  const blob = new Blob([JSON.stringify(baseline, null, 2)], {
    type: 'application/json',
  });
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}


// ----------------------------------------------------------------------
// Shared bits
// ----------------------------------------------------------------------

function StatCard({
  label,
  value,
  subtext,
  color,
  onClick,
  size = { xs: 12, sm: 4 },
}: {
  label: string;
  value: number;
  subtext: string;
  color?: string;
  /**
   * When set, the whole card becomes a CardActionArea — visual
   * hover affordance + a click target the operator can tap to
   * drill in. Same pattern as the DomainCards below the
   * Participants row on the tenant_admin Dashboard.
   */
  onClick?: () => void;
  /** Grid column span. Defaults to 3-across (sm: 4). */
  size?: React.ComponentProps<typeof Grid>['size'];
}) {
  const inner = (
    <CardContent sx={COMPACT_CARD_CONTENT}>
      <Typography
        variant="overline"
        sx={{
          fontWeight: 700,
          color: 'text.secondary',
          letterSpacing: '0.06em',
          display: 'block',
          lineHeight: 1.3,
        }}
      >
        {label}
      </Typography>
      {/* Subtext rides the number's baseline instead of taking its own
          row — that third line was a third of the card's height. */}
      <Stack direction="row" alignItems="baseline" spacing={0.5} sx={{ minWidth: 0 }}>
        <StatValue value={value} color={color} size={30} />
        {/* 0.7rem, not the 0.75rem default: at md each tile is 2/18 of the
            row, and "11 this week" beside a three-digit number clipped at
            the default size. Wrapping is not an option — the Grid stretches
            every sibling, so one card taking a second line raises all
            eight. */}
        <Typography
          variant="caption"
          color="text.secondary"
          noWrap
          sx={{ fontWeight: 600, minWidth: 0, fontSize: '0.7rem' }}
        >
          {subtext}
        </Typography>
      </Stack>
    </CardContent>
  );
  return (
    <Grid size={size} sx={{ display: 'flex' }}>
      {/* No coloured top edge. It sat on three of the eight cards only
          because those happen to be StatCards, which reads as "these three
          are special" when they are not — and the status colour is already
          carried by the number itself (Active green, Pending amber). An
          edge on all eight would spend the colour everywhere and leave the
          two that mean something no louder than the rest. */}
      <Card sx={{ width: '100%' }}>
        {onClick ? (
          <CardActionArea onClick={onClick} sx={{ height: '100%' }}>
            {inner}
          </CardActionArea>
        ) : (
          inner
        )}
      </Card>
    </Grid>
  );
}

function CountBlock({
  label,
  value,
  color,
}: {
  label: string;
  value: number;
  color?: string;
}) {
  return (
    <Stack>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <StatValue value={value} color={color} size={24} />
    </Stack>
  );
}
